import { i18n } from "@/i18n";
import {
  acceptHotItemStore,
  defineItemStore
} from "@editor/items/itemStore.store";

import { showConfirm } from "@core/ui/alert/alert";
import { useScenarioStore } from "@editor/items/scenario/scenarios.store";
import { useSimulationStore } from "@editor/items/simulation/simulations.store";
import { useEffectStore } from "@editor/items/effect/effects.store";
import { useImageStore } from "@editor/items/image/images.store";
import { useIncidentTriggerStore } from "@editor/items/incidentTrigger/incidentTriggers.store";

import type { IncidentItem } from "./IncidentItem";

const itemStructure: IncidentItem = {
  type: "incident",
  id: 0,
  uid: "incident_0",
  createdAt: -1,
  order: -1,

  title: "",
  description: "",
  chance: 10,
  maxOccurrences: null,
  imageId: null,

  visibility: "visible",

  effectsVisibility: "as-is"
};

export const useIncidentStore = defineItemStore(
  "model/incidents",
  itemStructure,
  {
    events: {
      async created(incident) {
        const scenarioStore = useScenarioStore();
        scenarioStore.connectAllToIncident({ incidentId: incident.id });
      },
      async canBeDeleted(incident) {
        const simulationStore = useSimulationStore();
        const simulationsWithIncident = simulationStore.orderedItems.filter(
          (sim) => sim.rounds.some((round) => round.activatedIncidents.some((activation) => activation.incidentId === incident.id))
        );
        if (simulationsWithIncident.length === 0) return true;
        return await showConfirm(
          i18n.t("questions.deleteIncident", { amount: simulationsWithIncident.length }),
          i18n.t("actions.deleteIncident"),
          i18n.t("labels.abort")
        );
      },
      afterDelete(incident) {
        const promises = [];
        // delete all effects that are connected with this incident
        const effectStore = useEffectStore();
        const effects = effectStore.byIncident[incident.id];
        if (effects != null) {
          promises.push(effectStore.deleteMany(effects));
        }

        // delete all triggers that are connected with this incident
        const triggerStore = useIncidentTriggerStore();
        const triggers = triggerStore.byIncident[incident.id];
        if (triggers != null) {
          promises.push(triggerStore.deleteMany(triggers));
        }

        // delete connected IMAGE
        if (incident.imageId != null) {
          const imageStore = useImageStore();
          promises.push(
            imageStore.delete({ id: incident.imageId, instant: true })
          );
        }

        // remove from scenarios
        const scenarioStore = useScenarioStore();
        const scenarios = scenarioStore.orderedItems.filter((scenario) => {
          return scenario.incidentIds.includes(incident.id);
        });
        for (const scenario of scenarios) {
          scenarioStore.disconnectIncident({
            id: scenario.id,
            incidentId: incident.id
          });
        }

        // delete all simulations with this incident
        const simulationStore = useSimulationStore();
        const simulationsWithIncident = simulationStore.orderedItems.filter(
          (sim) => sim.rounds.some((round) => round.activatedIncidents.some((activation) => activation.incidentId === incident.id))
        );
        for (const simulation of simulationsWithIncident) {
          const rounds = simulation.rounds;
          if (rounds.some((round) => round.incidentCounts?.[incident.id])) {
            // delete simulation if it has incidents in rounds
            promises.push(
              simulationStore.delete({ id: simulation.id, instant: true })
            );
          } else {
            // remove the incident id from simulation
            const incidentIds = simulation.incidentIds.filter((id) => id !== incident.id);
            simulationStore.update({ id: simulation.id, incidentIds });
          }
        }

        return Promise.all(promises);
      }
    },
    actions: {
      async duplicate(id: number, changes: Partial<IncidentItem> = {}) {
        const sourceItem = this.items[id];
        const duplicated = await this.super.duplicate(id, {
          imageId: null,
          ...changes
        });

        const effectStore = useEffectStore();
        const effects = effectStore.byIncident[id] ?? [];
        for (const effect of effects) {
          await effectStore.duplicate(effect.id, {
            activatorId: duplicated.id
          });
        }

        const triggerStore = useIncidentTriggerStore();
        const triggers = triggerStore.byIncident[id] ?? [];
        for (const trigger of triggers) {
          await triggerStore.duplicate(trigger.id, {
            incidentId: duplicated.id
          });
        }
        
        const imageStore = useImageStore();
        if (sourceItem.imageId != null) {
          const image = imageStore.items[sourceItem.imageId];
          if (image != null) {
            const imageCopy = await imageStore.duplicate(image.id);
            duplicated.imageId = imageCopy.id;
          }
        }

        return duplicated;
      }
    }
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(acceptHotItemStore(useIncidentStore, import.meta.hot));
}
